import { useCallback, useEffect, useState } from "react";
import {
  createPlatformCost,
  deletePlatformCost,
  endPlatformCost,
  listPlatformCosts,
  replacePlatformCost,
  updatePlatformCost,
} from "@/lib/platform/platformCostLedgerClient";
import type { PlatformCostDraft, PlatformCostLedgerRow } from "@/lib/platform/platformCostLedger";

export function usePlatformCostLedger() {
  const [rows, setRows] = useState<PlatformCostLedgerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schemaMissing, setSchemaMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const res = await listPlatformCosts();
    setLoading(false);
    if (!res.ok) {
      setSchemaMissing(res.schemaMissing);
      setError(res.error);
      setRows([]);
      return;
    }
    setSchemaMissing(false);
    setError(null);
    setRows(res.data.rows ?? []);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = useCallback(
    async (work: () => Promise<{ ok: true } | { ok: false; error: string; schemaMissing: boolean }>) => {
      setBusy(true);
      setError(null);
      const res = await work();
      setBusy(false);
      if (!res.ok) {
        setSchemaMissing(res.schemaMissing);
        setError(res.error);
        return false;
      }
      await refresh();
      return true;
    },
    [refresh],
  );

  const create = useCallback((draft: PlatformCostDraft) => run(() => createPlatformCost(draft)), [run]);
  const update = useCallback(
    (id: string, draft: PlatformCostDraft) => run(() => updatePlatformCost(id, draft)),
    [run],
  );
  const end = useCallback((id: string, endedOn: string) => run(() => endPlatformCost(id, endedOn)), [run]);
  const remove = useCallback((id: string) => run(() => deletePlatformCost(id)), [run]);
  const replace = useCallback(
    (id: string, draft: PlatformCostDraft) => run(() => replacePlatformCost(id, draft)),
    [run],
  );

  return { rows, loading, error, schemaMissing, busy, refresh, create, update, end, remove, replace };
}
